import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { VerticalTimeline } from "@/components/hoops/VerticalTimeline";
import { UpcomingEvents } from "@/components/hoops/UpcomingEvents";
import SocialStats from "@/components/blocks/SocialStats";
import InternationalCircuit from "@/components/blocks/InternationalCircuit";
import { HoopsNetwork } from "@/components/hoops/HoopsNetwork";
import larryHughesGraphic from "@/assets/larry-hughes-iverson-graphic.png";
import {
  Target,
  Users,
  Trophy,
  Star,
  ArrowRight,
  CheckCircle,
  Calendar,
  MapPin,
  Award,
  TrendingUp,
  Eye,
  Clock,
  Globe,
  Zap,
  Shield,
  Heart,
  Video,
  UserPlus
} from "lucide-react";

import { ExperienceHighlight } from "@/components/blocks/ExperienceHighlight";

export default function Test() {
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero Section */}
      <section 
        className="py-16 md:py-20 text-white relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 30, 60, 0.9), rgba(0, 20, 40, 0.8)), url('/lovable-uploads/dae4f708-da1f-4396-9382-c1d88fe9441b.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black mb-6 md:mb-8 leading-tight">
              RECRUITLOOK
              <span className="block text-secondary">HOOPS</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Prominent organization dedicated to basketball tournaments, camps, and recruiting services. 
              Hosting boys' high school and middle school tournaments nationwide with coast-to-coast coverage.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Buttons */}
      <section className="pt-8 pb-8 md:pt-12 md:pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/boys-circuit">BOYS CIRCUIT</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/girls-circuit">GIRLS CIRCUIT</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/larry-hughes-elite-camp">ELITE CAMP</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/juniors-circuit">JUNIORS CIRCUIT</a>
            </Button>
          </div>
          
          {/* Application Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4 md:mt-6">
            <Button 
              asChild
              className="bg-primary hover:bg-primary-600 text-white font-bold px-4 md:px-6 py-2 md:py-3 text-xs tracking-wide w-full sm:w-auto"
            >
              <a href="https://form.jotform.com/252124825226149" target="_blank" rel="noopener noreferrer">
                Girls Circuit Application
              </a>
            </Button>
            <Button 
              asChild
              className="bg-primary hover:bg-primary-600 text-white font-bold px-4 md:px-6 py-2 md:py-3 text-xs tracking-wide w-full sm:w-auto"
            >
              <a href="https://www.jotform.com/252124775960158" target="_blank" rel="noopener noreferrer">
                Boys Circuit Application
              </a>
            </Button>
            <Button 
              asChild
              className="bg-primary hover:bg-primary-600 text-white font-bold px-4 md:px-6 py-2 md:py-3 text-xs tracking-wide w-full sm:w-auto"
            >
              <a href="https://form.jotform.com/252125181995158" target="_blank" rel="noopener noreferrer">
                Juniors Application
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* RecruitLook Hoops Circuit Banner */}
      <section className="w-full relative bg-white overflow-visible">
        <div className="relative h-20 md:h-24 lg:h-28">
          <div className="absolute inset-0 bg-secondary" />
          <div className="absolute inset-0 flex items-center justify-center gap-4 md:gap-6 px-4">
            <img
              src="https://cdn.builder.io/api/v1/image/assets%2F7b5b7573f41a45539311717edd1f85db%2F7152b0dd4db2485282ccd48ece221b4a?format=webp&width=800"
              alt="RecruitLook Hoops logo"
              className="h-16 md:h-20 lg:h-24 w-auto pointer-events-none select-none"
            />
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-black uppercase leading-none tracking-tight text-slate-900">
              RECRUITLOOK <span className="text-white">HOOPS</span> CIRCUIT
            </h2>
          </div>
        </div>
      </section>

      {/* Experience Block (imported from external inspiration) */}
      <ExperienceHighlight />


      {/* Upcoming Events */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 md:mb-6 text-slate-900">
              UPCOMING
              <span className="block text-secondary">EVENTS</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Join us at exclusive basketball tournaments, camps, and showcases nationwide
            </p>
          </div>

          <UpcomingEvents sportSlug="basketball" sportName="basketball" />
        </div>
      </section>

      {/* Timeline Section - moved below hoops circuit */}
      <section className="bg-white">
      {/* Timeline Header Gold Bar */}
        <div className="w-full relative bg-white overflow-visible mb-8">
          <div className="relative h-20 md:h-24 lg:h-28">
            <div className="absolute inset-0 bg-secondary" />
            <div className="absolute inset-0 flex flex-col items-center justify-center px-4 py-3">
              <h2 className="text-xl md:text-2xl lg:text-3xl xl:text-4xl font-black uppercase leading-none tracking-tight text-slate-900 text-center">
                RECRUITLOOK HOOPS <span className="text-white">TIMELINE</span>
              </h2>
              <p className="text-xs md:text-sm lg:text-base text-slate-800 font-semibold text-center max-w-4xl mt-2">
                Two decades of connecting basketball talent with collegiate opportunities, building the most comprehensive recruiting network in the nation.
              </p>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <VerticalTimeline
            items={[
              { year: 2005, text: "The launch of RecruitLook" },
              { year: 2008, text: "Mike Enright starts scouting" },
              { year: 2009, text: "2 AAU tournaments annually" },
              { year: 2010, text: "Online scouting portal" },
              { year: 2013, text: "4 AAU tournaments annually" },
              { year: 2016, text: "First JUCO Showcase" },
              { year: 2017, text: "Launch of Hoops Circuit" },
              { year: 2021, text: "10 tourneys, 1,000 teams" },
              { year: 2022, text: "13 tourneys, 1,500 teams" },
              { year: 2024, text: "21 tourneys, 2,000+ teams" },
              { year: 2025, text: "Added 3 Girls Events" },
            ]}
          />
        </div>
      </section>

      <HoopsNetwork />

      {/* College Coaches Statistic Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-4">
              OVER <span className="text-secondary">1,000</span> COLLEGE COACHES
            </h2>
            <p className="text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-700">
              2025: attended a RecruitLook tournament in 2025
            </p>
            <div className="mt-6 md:mt-8 flex justify-center">
              <div className="w-16 md:w-24 h-1 bg-secondary rounded-full"></div>
            </div>
            
            {/* College Logos Grid */}
            <div className="mt-12 md:mt-16">
              <img 
                src="/lovable-uploads/782fe955-78f8-4ba8-9743-4387f4f7c63f.png"
                alt="College logos of coaches who attended RecruitLook tournaments"
                className="w-full max-w-6xl mx-auto h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social media stats section */}
      <SocialStats />

      {/* Larry Hughes Elite Camp Feature */}
      <section className="py-16 bg-gradient-to-br from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
                LARRY HUGHES
                <span className="block text-secondary">ELITE CAMP</span>
              </h2>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>
                  Initiated in 2018, this invite-only camp selects 60 of the nation's top high school players 
                  annually, with participants from 20 different states attending the elite event.
                </p>
                <p>
                  The camp has received coverage from national media outlets, and many players have secured 
                  their first collegiate offers post-camp. It's the only camp partnered with Allen Iverson's group.
                </p>
                <p>
                  The camp MVP is chosen to participate in the prestigious Iverson Classic, creating a direct 
                  pathway to elite basketball exposure and opportunity.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 my-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="text-3xl font-black text-secondary mb-2">60</div>
                  <div className="text-slate-900 font-bold">Elite Players</div>
                  <div className="text-sm text-gray-600">selected annually</div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="text-3xl font-black text-secondary mb-2">20</div>
                  <div className="text-slate-900 font-bold">States</div>
                  <div className="text-sm text-gray-600">represented</div>
                </div>
              </div>

            </div>
            
            <div className="relative">
              <img 
                src={larryHughesGraphic}
                alt="Larry Hughes Elite Camp - Iverson Classic Evaluation & Qualifier Camp"
                className="w-full h-auto object-contain rounded-2xl shadow-lg"
              />
              <Badge className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 font-black">
                INVITE ONLY
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* International circuit map */}
      <InternationalCircuit />




      {/* Call to Action */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-100 to-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-6 md:mb-8 text-slate-900">
            READY TO TAKE
            <span className="block text-secondary">YOUR SHOT?</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-8 md:mb-12 max-w-2xl mx-auto leading-relaxed">
            Join thousands of basketball players who have elevated their recruiting prospects through RecruitLook Hoops events and exposure.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 md:space-x-8 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-secondary" />
              <span className="font-semibold">800+ College Coaches</span>
            </div>
            <div className="flex items-center space-x-2">
              <Trophy className="w-5 h-5 text-secondary" />
              <span className="font-semibold">Elite Competition</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-secondary" />
              <span className="font-semibold">National Coverage</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
